import { nextTick, onBeforeUnmount, ref, watch } from 'vue'
import type { TermItem } from './types'

export type { TermItem }

export type CounselForm = {
  values: Record<string, string>
  validate: () => Promise<{ errors: Record<string, boolean> }>
  getValues: () => { name: string; phoneNumber: string }
}

const isEmpty = (v: unknown): boolean => {
  if (v == null) return true
  if (typeof v === 'string') return v === ''
  if (Array.isArray(v)) return v.length === 0
  if (typeof v === 'object') return Object.keys(v as object).length === 0
  return false
}

export function useQuickRegister() {
  const termsLists = ref<TermItem[]>([])
  /** 약관 전체 동의 상태 (모든 약관이 체크되었는지) */
  const isAllCheckAgree = ref(false)
  /** 폼 입력값 검증 + 필수 약관 동의 모두 충족 */
  const isAllWrite = ref(false)
  /** 필수 약관만 모두 동의되었는지 (선택 약관은 무관) */
  const isRequiredAllCheck = ref(false)
  const selectTerm = ref<TermItem | null>(null)
  const isTermsModal = ref(false)
  const counselForm = ref<CounselForm | null>(null)
  const isModal = ref(false)
  const confirmTimer = ref<ReturnType<typeof setTimeout> | null>(null)
  const isFormValid = ref(false)

  const openTermsModal = (term: TermItem) => {
    selectTerm.value = term
    isTermsModal.value = true
  }

  const closeTermsModal = () => {
    isTermsModal.value = false
    selectTerm.value = null
  }

  const toggleAllAgree = (value: boolean) => {
    isAllCheckAgree.value = value
    termsLists.value.forEach(item => {
      item.isCheck = value
    })
  }

  watch(
    () => counselForm.value?.values,
    async () => {
      if (!counselForm.value) return
      const hasEmptyField = Object.values(counselForm.value.values).some(isEmpty)
      if (hasEmptyField) {
        isAllWrite.value = false
        return
      }
      const { errors } = await counselForm.value.validate()
      isFormValid.value = isEmpty(errors)
      isAllWrite.value = isFormValid.value && isRequiredAllCheck.value
    },
    { deep: true },
  )

  watch(
    termsLists,
    () => {
      const list = termsLists.value
      isAllCheckAgree.value = list.length > 0 && list.every(item => item.isCheck)
      isRequiredAllCheck.value =
        list.length > 0 && list.every(item => !item.isRequired || item.isCheck)
      isAllWrite.value = isFormValid.value && isRequiredAllCheck.value
    },
    { deep: true },
  )

  onBeforeUnmount(() => {
    nextTick(() => {
      if (confirmTimer.value) clearTimeout(confirmTimer.value)
    })
  })

  return {
    termsLists,
    isAllCheckAgree,
    isAllWrite,
    isRequiredAllCheck,
    selectTerm,
    isTermsModal,
    counselForm,
    isModal,
    confirmTimer,
    openTermsModal,
    closeTermsModal,
    toggleAllAgree,
  }
}
